import { useMutation, useQueryClient } from "@tanstack/react-query";
import { isAxiosError } from "axios";
import { enqueueSnackbar } from "notistack";
import {
    updateExtractedProperty,
    deleteExtractedProperty,
    batchDeleteExtractedProperties,
} from "@/api/scopes";
import type {
    IExtractedPropertyUpdate,
    IBatchDeleteRequest,
} from "@/api/scopes";
import {
    addDocumentCustomFieldProperty,
    createDocumentCustomField,
    deleteDocumentCustomField,
    deleteDocumentCustomFieldProperty,
    updateDocumentCustomFieldProperty,
} from "@/api/documents";
import type {
    IDocumentCustomFieldCreate,
    IDocumentCustomFieldPropertyCreate,
} from "@/api/documents/documents.types";

export const useFieldMutations = (scopeId: number, documentId: number) => {
    const queryClient = useQueryClient();
    const queryKey = ["scope-extracted-fields", scopeId];

    const onSuccess = () => {
        queryClient.invalidateQueries({ queryKey });
    };

    const updateProperty = useMutation({
        mutationFn: ({
            propertyId,
            data,
        }: {
            propertyId: number;
            data: IExtractedPropertyUpdate;
        }) => updateExtractedProperty(scopeId, propertyId, data),
        onSuccess,
        onError: () => {
            enqueueSnackbar("Failed to update field", { variant: "error" });
        },
    });

    const deleteProperty = useMutation({
        mutationFn: (propertyId: number) =>
            deleteExtractedProperty(scopeId, propertyId),
        onSuccess,
        onError: () => {
            enqueueSnackbar("Failed to delete field", { variant: "error" });
        },
    });

    const batchDeleteProperties = useMutation({
        mutationFn: (data: IBatchDeleteRequest) =>
            batchDeleteExtractedProperties(scopeId, data),
        onSuccess,
        onError: (error) => {
            if (isAxiosError(error) && error.response?.status === 409) {
                queryClient.invalidateQueries({ queryKey });
                enqueueSnackbar(
                    "This item changed. Review the refreshed fields before deleting.",
                    {
                        variant: "error",
                    }
                );
                return;
            }
            enqueueSnackbar("Failed to delete array item", {
                variant: "error",
            });
        },
    });

    const createField = useMutation({
        mutationFn: (data: IDocumentCustomFieldCreate) =>
            createDocumentCustomField(documentId, data),
        onSuccess,
        onError: () => {
            enqueueSnackbar("Failed to create custom field", {
                variant: "error",
            });
        },
    });

    const deleteField = useMutation({
        mutationFn: (customFieldId: number) =>
            deleteDocumentCustomField(documentId, customFieldId),
        onSuccess,
        onError: () => {
            enqueueSnackbar("Failed to delete custom field", {
                variant: "error",
            });
        },
    });

    const addCFProperty = useMutation({
        mutationFn: ({
            customFieldId,
            data,
        }: {
            customFieldId: number;
            data: IDocumentCustomFieldPropertyCreate;
        }) => addDocumentCustomFieldProperty(documentId, customFieldId, data),
        onSuccess,
        onError: () => {
            enqueueSnackbar("Failed to add property", { variant: "error" });
        },
    });

    const updateCFProperty = useMutation({
        mutationFn: ({
            propertyId,
            data,
        }: {
            propertyId: number;
            data: { value?: string | null; page_number?: number | null };
        }) => updateDocumentCustomFieldProperty(documentId, propertyId, data),
        onSuccess,
        onError: () => {
            enqueueSnackbar("Failed to update property", { variant: "error" });
        },
    });

    const deleteCFProperty = useMutation({
        mutationFn: (propertyId: number) =>
            deleteDocumentCustomFieldProperty(documentId, propertyId),
        onSuccess,
        onError: () => {
            enqueueSnackbar("Failed to delete property", {
                variant: "error",
            });
        },
    });

    return {
        updateProperty,
        deleteProperty,
        batchDeleteProperties,
        createField,
        deleteField,
        addCFProperty,
        updateCFProperty,
        deleteCFProperty,
    };
};
